/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import type { AgencyStatus } from '@/features/onboarding/types/agency-dashboard';

type BadgeStyle = {
  bg: string;
  text: string;
  border: string;
};

const STATUS_STYLES: Record<AgencyStatus, BadgeStyle> = {
  active: {
    bg: 'color-mix(in srgb, var(--status-success-text) 12%, transparent)',
    text: 'var(--status-success-text)',
    border: 'color-mix(in srgb, var(--status-success-text) 30%, transparent)',
  },
  payment_failed: {
    bg: 'color-mix(in srgb, var(--status-payment-failure) 12%, transparent)',
    text: 'var(--status-payment-failure)',
    border: 'color-mix(in srgb, var(--status-payment-failure) 30%, transparent)',
  },
  no_subscription: {
    bg: 'color-mix(in srgb, var(--text-tertiary) 10%, transparent)',
    text: 'var(--text-secondary)',
    border: 'color-mix(in srgb, var(--text-tertiary) 25%, transparent)',
  },
  cancelled: {
    bg: 'color-mix(in srgb, var(--status-payment-failure) 12%, transparent)',
    text: 'var(--status-payment-failure)',
    border: 'color-mix(in srgb, var(--status-payment-failure) 30%, transparent)',
  },
  suspended: {
    bg: 'color-mix(in srgb, var(--status-payment-warning) 12%, transparent)',
    text: 'var(--status-payment-warning)',
    border: 'color-mix(in srgb, var(--status-payment-warning) 30%, transparent)',
  },
  pending_upgrade: {
    bg: 'color-mix(in srgb, var(--seed-blue) 12%, transparent)',
    text: 'var(--seed-blue)',
    border: 'color-mix(in srgb, var(--seed-blue) 30%, transparent)',
  },
  trialing: {
    bg: 'color-mix(in srgb, var(--seed-blue) 12%, transparent)',
    text: 'var(--seed-blue)',
    border: 'color-mix(in srgb, var(--seed-blue) 30%, transparent)',
  },
};

const STATUS_KEYS: Record<AgencyStatus, string> = {
  active: 'subscriptionStatus.badge.active',
  payment_failed: 'subscriptionStatus.badge.paymentFailed',
  no_subscription: 'subscriptionStatus.badge.noSubscription',
  cancelled: 'subscriptionStatus.badge.cancelled',
  suspended: 'subscriptionStatus.badge.suspended',
  pending_upgrade: 'subscriptionStatus.badge.pendingUpgrade',
  trialing: 'subscriptionStatus.badge.trialing',
};

interface SubscriptionStatusBadgeProps {
  status: AgencyStatus;
  className?: string;
}

export const SubscriptionStatusBadge: React.FC<SubscriptionStatusBadgeProps> = ({ status, className }) => {
  const { t } = useTranslation('@pages/manage-agencies');
  const style = STATUS_STYLES[status];
  if (!style) return null;

  const label = t(STATUS_KEYS[status]);

  return (
    <Text
      as="span"
      role="status"
      aria-label={`Status: ${label}`}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: 'var(--space-1) var(--space-2-5)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--text-meta)',
        fontWeight: 600,
        lineHeight: '1rem',
        border: `1px solid ${style.border}`,
        backgroundColor: style.bg,
        color: style.text,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Text>
  );
};
